import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Dialog, DialogActions, DialogContent, MenuItem, Switch, TextField
} from "@material-ui/core";
import { ModalTitle, FormField, FormGrid, FormSection, SwitchRow } from "../../components/QueueIntegrationModal/panelKit";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const t = k => i18n.t(`erpWriteback.${k}`);
const ACTIONS = [["none", "actionNone"], ["note", "actionNote"], ["service_order", "actionOs"]];
const TRIGGERS = [["close", "triggerClose"], ["transfer", "triggerTransfer"], ["close_and_transfer", "triggerBoth"], ["manual", "triggerManual"]];
const EMPTY = { queueId: "", erpIntegrationId: "", action: "note", trigger: "close", erpTypeId: "", erpSectorId: "", extraFields: "{}", includeLink: true, enabled: true };

const ErpWritebackRuleModal = ({ open, onClose, rule, queues, integrations, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [support, setSupport] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      setForm({
        queueId: rule.queueId || "", erpIntegrationId: rule.erpIntegrationId || "", action: rule.action || "note", trigger: rule.trigger || "close",
        erpTypeId: rule.erpTypeId || "", erpSectorId: rule.erpSectorId || "", extraFields: JSON.stringify(rule.extraFields || {}, null, 0),
        includeLink: rule.includeLink !== false, enabled: rule.enabled !== false
      });
    } else {
      setForm({ ...EMPTY, erpIntegrationId: integrations[0]?.id || "" });
    }
  }, [open, rule, integrations]);

  useEffect(() => {
    if (!open || !form.erpIntegrationId) { setSupport(null); return; }
    let alive = true;
    api.get("/erp-writeback/support", { params: { integrationId: form.erpIntegrationId } })
      .then(({ data }) => { if (alive) setSupport(data); })
      .catch(() => { if (alive) setSupport(null); });
    return () => { alive = false; };
  }, [open, form.erpIntegrationId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const unsupported = support && ((form.action === "note" && !support.note) || (form.action === "service_order" && !support.serviceOrder));

  const handleSave = async () => {
    let extra = {};
    try { extra = form.extraFields ? JSON.parse(form.extraFields) : {}; } catch { return toast.error(t("extraInvalid")); }
    setSaving(true);
    try {
      const payload = { ...form, queueId: form.queueId === "" ? null : Number(form.queueId), erpIntegrationId: Number(form.erpIntegrationId), extraFields: extra };
      if (rule?.id) await api.put(`/erp-writeback/rules/${rule.id}`, payload);
      else await api.post("/erp-writeback/rules", payload);
      toast.success(t("saved"));
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const supportWarning = unsupported
    ? `${t("supportWarn").replace("{{what}}", form.action === "note" ? t("actionNote") : t("actionOs"))}${support.notes ? ` — ${support.notes}` : ""}`
    : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <ModalTitle title={rule?.id ? t("editRule") : t("newRule")} onClose={onClose} />
      <DialogContent>
        <FormSection>
          <FormGrid>
            <FormField label={t("queue")}>
              <TextField
                select fullWidth size="small" variant="outlined"
                value={form.queueId} onChange={e => set("queueId", e.target.value)}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">{t("defaultRule")}</MenuItem>
                {queues.map(q => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
              </TextField>
            </FormField>
            <FormField label={t("integration")} required>
              <TextField
                select fullWidth size="small" variant="outlined"
                value={form.erpIntegrationId} onChange={e => set("erpIntegrationId", e.target.value)}
              >
                {integrations.map(i => <MenuItem key={i.id} value={i.id}>{i.name} ({i.type})</MenuItem>)}
              </TextField>
            </FormField>
            <FormField label={t("action")} error={supportWarning}>
              <TextField
                select fullWidth size="small" variant="outlined" error={!!unsupported}
                value={form.action} onChange={e => set("action", e.target.value)}
              >
                {ACTIONS.map(([v, k]) => <MenuItem key={v} value={v}>{t(k)}</MenuItem>)}
              </TextField>
            </FormField>
            <FormField label={t("trigger")}>
              <TextField
                select fullWidth size="small" variant="outlined"
                value={form.trigger} onChange={e => set("trigger", e.target.value)}
              >
                {TRIGGERS.map(([v, k]) => <MenuItem key={v} value={v}>{t(k)}</MenuItem>)}
              </TextField>
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection title="Dados enviados ao ERP">
          <FormGrid>
            <FormField label={t("erpTypeId")} help={t("erpTypeHint")}>
              <TextField fullWidth size="small" variant="outlined" value={form.erpTypeId} onChange={e => set("erpTypeId", e.target.value)} />
            </FormField>
            <FormField label={t("erpSectorId")}>
              <TextField fullWidth size="small" variant="outlined" value={form.erpSectorId} onChange={e => set("erpSectorId", e.target.value)} />
            </FormField>
            <FormField full label={t("extraFields")} help={t("extraHint")}>
              <TextField fullWidth size="small" variant="outlined" multiline minRows={2} value={form.extraFields} onChange={e => set("extraFields", e.target.value)} />
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection>
          <FormGrid>
            <SwitchRow label={t("includeLink")}>
              <Switch checked={form.includeLink} onChange={e => set("includeLink", e.target.checked)} color="primary" inputProps={{ "aria-label": t("includeLink") }} />
            </SwitchRow>
            <SwitchRow label={t("active")}>
              <Switch checked={form.enabled} onChange={e => set("enabled", e.target.checked)} color="primary" inputProps={{ "aria-label": t("active") }} />
            </SwitchRow>
          </FormGrid>
        </FormSection>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving || !form.erpIntegrationId}>{t("save")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErpWritebackRuleModal;
